import React, { useState, useEffect } from "react";
import { Program, UpdateProgramData } from "../../services/api";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useSystemCategories } from "../../hooks/useCategories";

// Category pair type for the new UI
interface CategoryPair {
  id: string;
  category: string;
  subcategory: string;
}

interface ProgramFormProps {
  program: Program | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UpdateProgramData) => void;
  isLoading?: boolean;
}

const ProgramForm: React.FC<ProgramFormProps> = ({
  program,
  isOpen,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<UpdateProgramData>({
    descriptionCustomer: "",
    conditions: "",
    cashbackRate: 0,
    cashbackType: "percent",
    cashbackFixedAmount: 0,
    countries: [],
    priority: 0,
    category: "",
    subcategory: "",
    categories: [],
    subcategories: [],
  });
  const [countriesInput, setCountriesInput] = useState<string>("");
  const [categoryPairs, setCategoryPairs] = useState<CategoryPair[]>([]);

  const { data: systemCategories, isLoading: isCategoriesLoading } =
    useSystemCategories();

  // Get subcategories for a specific category
  const getSubcategoriesForCategory = (categoryName: string): string[] => {
    if (!categoryName || !systemCategories) return [];
    const cat = systemCategories.find((c) => c.categoryName === categoryName);
    if (!cat?.subcategories) return [];
    return cat.subcategories.map((sub) =>
      typeof sub === "object" ? sub.name : sub,
    );
  };

  // Generate unique ID for category pairs
  const generateId = () => Math.random().toString(36).substr(2, 9);

  useEffect(() => {
    if (program) {
      // Handle legacy single category/subcategory
      const legacyCategory =
        typeof program.category === "object" && program.category !== null
          ? (program.category as any).name ||
            (program.category as any).categoryName ||
            ""
          : program.category || "";
      const legacySubcategory =
        typeof program.subcategory === "object" && program.subcategory !== null
          ? (program.subcategory as any).name || ""
          : program.subcategory || "";

      // Build category pairs from arrays or legacy
      const categories = program.categories || [];
      const subcategories = program.subcategories || [];

      let pairs: CategoryPair[] = [];

      if (categories.length > 0) {
        // Create pairs from categories and subcategories arrays
        // Each category gets paired with corresponding subcategory (if exists)
        pairs = categories.map((cat, index) => ({
          id: generateId(),
          category: cat,
          subcategory: subcategories[index] || "",
        }));
      } else if (legacyCategory) {
        // Fall back to legacy single category
        pairs = [
          {
            id: generateId(),
            category: legacyCategory,
            subcategory: legacySubcategory,
          },
        ];
      }

      setCategoryPairs(pairs);

      setFormData({
        descriptionCustomer: program.descriptionCustomer || "",
        conditions: program.conditions || "",
        cashbackRate: program.cashbackRate || 0,
        cashbackType: program.cashbackType || "percent",
        cashbackFixedAmount: program.cashbackFixedAmount || 0,
        countries: program.countries || [],
        priority: program.priority || 0,
        category: legacyCategory,
        subcategory: legacySubcategory,
        categories,
        subcategories,
      });
      setCountriesInput(program.countries?.join(", ") || "");
    }
  }, [program]);

  // Sync category pairs to formData
  useEffect(() => {
    const categories = categoryPairs.map((p) => p.category).filter(Boolean);
    const subcategories = categoryPairs
      .map((p) => p.subcategory)
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      categories,
      subcategories,
      category: categories[0] || "",
      subcategory: subcategories[0] || "",
    }));
  }, [categoryPairs]);

  // Add new category pair
  const handleAddCategoryPair = () => {
    setCategoryPairs((prev) => [
      ...prev,
      { id: generateId(), category: "", subcategory: "" },
    ]);
  };

  // Remove category pair
  const handleRemoveCategoryPair = (id: string) => {
    setCategoryPairs((prev) => prev.filter((p) => p.id !== id));
  };

  // Update category in pair
  const handleCategoryChange = (id: string, categoryName: string) => {
    setCategoryPairs((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, category: categoryName, subcategory: "" } : p,
      ),
    );
  };

  // Update subcategory in pair
  const handleSubcategoryChange = (id: string, subcategoryName: string) => {
    setCategoryPairs((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, subcategory: subcategoryName } : p,
      ),
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "cashbackRate" || name === "cashbackFixedAmount"
          ? parseFloat(value) || 0
          : name === "priority"
            ? parseInt(value) || 0
            : value,
    }));
  };

  const handleCountriesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCountriesInput(value);
    const countries = value
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c);
    setFormData((prev) => ({
      ...prev,
      countries,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit {program?.name}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Customer Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Description
                </label>
                <textarea
                  name="descriptionCustomer"
                  value={formData.descriptionCustomer}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                  rows={3}
                  placeholder="Enter customer description..."
                />
              </div>

              {/* Conditions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Conditions
                </label>
                <textarea
                  name="conditions"
                  value={formData.conditions}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                  rows={2}
                  placeholder="Enter conditions..."
                />
              </div>

              {/* Cashback Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cashback
                </label>

                {/* Cashback Type Radio Buttons */}
                <div className="flex items-center space-x-6 mb-3">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="cashbackType"
                      value="percent"
                      checked={formData.cashbackType === "percent"}
                      onChange={handleInputChange}
                      className="form-radio h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Percent
                    </span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="cashbackType"
                      value="fixed"
                      checked={formData.cashbackType === "fixed"}
                      onChange={handleInputChange}
                      className="form-radio h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Fixed amount
                    </span>
                  </label>
                </div>

                {/* Cashback Input Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <input
                      type="number"
                      name="cashbackRate"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.cashbackRate || ""}
                      onChange={handleInputChange}
                      disabled={formData.cashbackType !== "percent"}
                      className={`w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 pr-8 ${
                        formData.cashbackType !== "percent"
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="0.0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                      %
                    </span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      name="cashbackFixedAmount"
                      min="0"
                      step="0.01"
                      value={formData.cashbackFixedAmount || ""}
                      onChange={handleInputChange}
                      disabled={formData.cashbackType !== "fixed"}
                      className={`w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 pr-10 ${
                        formData.cashbackType !== "fixed"
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                      EP
                    </span>
                  </div>
                </div>
              </div>

              {/* Countries */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Countries
                </label>
                <input
                  type="text"
                  name="countries"
                  value={countriesInput}
                  onChange={handleCountriesChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                  placeholder="DE, AT, CH"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Separate countries with commas
                </p>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <input
                  type="number"
                  name="priority"
                  min="0"
                  step="1"
                  value={formData.priority || ""}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Lower numbers appear first (1 = highest priority)
                </p>
              </div>

              {/* Categories Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Categories
                  </label>
                  <button
                    type="button"
                    onClick={handleAddCategoryPair}
                    className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add
                  </button>
                </div>

                {/* Category Pairs */}
                <div className="space-y-3">
                  {categoryPairs.map((pair) => (
                    <div
                      key={pair.id}
                      className="flex items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-600"
                    >
                      {/* Main Category */}
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-10">
                          Main
                        </span>
                        <select
                          value={pair.category}
                          onChange={(e) =>
                            handleCategoryChange(pair.id, e.target.value)
                          }
                          disabled={isCategoriesLoading}
                          className="flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                        >
                          <option value="">Select category...</option>
                          {systemCategories?.map((cat) => (
                            <option key={cat._id} value={cat.categoryName}>
                              {cat.categoryName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Subcategory */}
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-8">
                          Sub:
                        </span>
                        <select
                          value={pair.subcategory}
                          onChange={(e) =>
                            handleSubcategoryChange(pair.id, e.target.value)
                          }
                          disabled={!pair.category}
                          className={`flex-1 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 ${
                            !pair.category
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <option value="">Select subcategory...</option>
                          {getSubcategoriesForCategory(pair.category).map(
                            (sub, idx) => (
                              <option key={idx} value={sub}>
                                {sub}
                              </option>
                            ),
                          )}
                        </select>
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => handleRemoveCategoryPair(pair.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}

                  {categoryPairs.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
                      No categories added. Click "+ Add" to add a category.
                    </p>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={() => onSave(formData)}
              disabled={isLoading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramForm;
